import {
  DataGrid,
  FilterToolbar,
  formatDate,
  Modal,
  ModalBody,
  StatusBadge,
  tryParse,
  useNotifications
} from '@ijoin/components';
import { ConnectivityCheckItem, GetAdminConnectivityRes } from '@ijoin/hooks';
import { InfoOutlined } from '@mui/icons-material';
import { IconButton, Stack, styled, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { FC, useState } from 'react';

import { LinkButton } from './LinkButton';

const Pre = styled('pre')({
  margin: 0,
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word'
});

export const ConnectivityChecks: FC<{
  label: string;
  /** link the integration cell to the integration details page (default true) */
  linkIntegration?: boolean;
  queryKey: string;
  showHost?: boolean;
  showOrganizations?: boolean;
  /** initial sort column (default 'description') */
  sortField?: string;
  testId: string;
  useQuery: (options?: never) => {
    data?: GetAdminConnectivityRes;
    isFetching: boolean;
  };
  useRefresh: (options: {
    mutation: { onError: () => void; onSuccess: () => void };
  }) => { isPending: boolean; mutate: () => void };
}> = props => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const query = props.useQuery();
  const [details, setDetails] = useState<ConnectivityCheckItem | null>(null);
  const parsedDetails = tryParse<unknown>(details?.details ?? '');

  const refresh = props.useRefresh({
    mutation: {
      onError: () =>
        notifications.error(`Failed to refresh ${props.label} connectivity`),
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: [props.queryKey] })
    }
  });

  const onRefresh = () => refresh.mutate();

  const onDownload = () => window.open(`${props.queryKey}/export`, '_blank');

  const onCloseDetails = () => setDetails(null);

  const isLoading = query.isFetching || refresh.isPending;

  const columns: GridColDef<ConnectivityCheckItem>[] = [
    ...((props.showOrganizations ?? true)
      ? [
          {
            field: 'advisoryOrganizations',
            flex: 1.5,
            headerName: 'Advisory Organization',
            renderCell: params => (
              <Stack
                alignItems='center'
                direction='row'
                spacing={1}
                sx={{ height: '100%' }}>
                {params.row.advisoryOrganizations.map(organization => (
                  <LinkButton
                    data-testid={`organization-${organization.advisoryOrganizationId}`}
                    key={organization.advisoryOrganizationId}
                    params={{
                      advisoryOrganizationId:
                        organization.advisoryOrganizationId
                    }}
                    to='/organizations/$advisoryOrganizationId'>
                    {organization.name}
                  </LinkButton>
                ))}
              </Stack>
            ),
            valueGetter: (_value, row) =>
              row.advisoryOrganizations
                .map(organization => organization.name)
                .join(', ')
          } satisfies GridColDef<ConnectivityCheckItem>
        ]
      : []),
    {
      field: 'description',
      flex: 2,
      headerName: 'Integration',
      ...((props.linkIntegration ?? true) && {
        renderCell: params => (
          <Stack alignItems='center' direction='row' sx={{ height: '100%' }}>
            <LinkButton
              data-testid={`integration-${params.row.integrationId}`}
              params={{ integrationId: params.row.integrationId }}
              to='/integrations/$integrationId'>
              {params.row.description ?? params.row.integrationId}
            </LinkButton>
          </Stack>
        )
      })
    },
    ...(props.showHost
      ? [
          {
            field: 'host',
            flex: 1.5,
            headerName: 'Host'
          } satisfies GridColDef<ConnectivityCheckItem>
        ]
      : []),
    {
      field: 'integrationType',
      flex: 1,
      headerName: 'Type'
    },
    {
      field: 'success',
      flex: 1,
      headerName: 'Status',
      renderCell: params => (
        <StatusBadge severity={params.row.success ? 'success' : 'error'}>
          {params.row.success ? 'Connected' : 'Failed'}
        </StatusBadge>
      )
    },
    {
      field: 'details',
      headerName: '',
      renderCell: params =>
        params.row.success ? null : (
          <Tooltip title='View response details'>
            <IconButton
              data-testid={`details-${params.row.integrationId}`}
              onClick={() => setDetails(params.row)}>
              <InfoOutlined />
            </IconButton>
          </Tooltip>
        ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
      {query.data?.checkedAt && (
        <Typography color='text.secondary' variant='body2'>
          Last checked{' '}
          {formatDate(query.data.checkedAt, {
            format: 'MM/DD/YYYY h:mm:ss A'
          })}
        </Typography>
      )}

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.integrationId}
        initialState={{
          sorting: {
            sortModel: [
              { field: props.sortField ?? 'description', sort: 'asc' }
            ]
          }
        }}
        loading={isLoading}
        rows={query.data?.data ?? []}
        showToolbar
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': props.testId
          },
          toolbar: {
            downloadTestId: `export-${props.testId}`,
            onDownload: query.data?.data.some(item => !item.success)
              ? onDownload
              : undefined,
            onRefresh,
            refreshTestId: `refresh-${props.testId}`
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sx={{ flex: 1, minHeight: 0 }}
      />

      <Modal
        data-testid='connectivityDetails'
        hasCloseBtn
        header={details?.description ?? 'Response Details'}
        isOpen={!!details}
        onClose={onCloseDetails}>
        <ModalBody>
          <Pre>
            {parsedDetails
              ? JSON.stringify(parsedDetails, null, 2)
              : details?.details}
          </Pre>
        </ModalBody>
      </Modal>
    </Stack>
  );
};

ConnectivityChecks.displayName = 'ConnectivityChecks';
